import React, { useState } from 'react';
import { Article } from '../../types';
import { formatVideoDuration } from '../../utils/blogFormat';
import { aparatShareToEmbedUrl } from '../../utils/aparatEmbed';
import BlogIcon from './BlogIcon';
import { useBlogSettings } from './blogSettings';

interface ArticleVideoProps {
  article: Article;
}

/** بلوک «ویدیوی مرتبط با این مقاله»: پوستر + پلی، پخش آپارات/فایل، حالت خطا */
const ArticleVideo: React.FC<ArticleVideoProps> = ({ article }) => {
  const { videoTitle } = useBlogSettings();
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const videoUrl = article.videoUrl || '';
  if (!videoUrl) return null;
  const embedUrl = aparatShareToEmbedUrl(videoUrl);
  const poster = article.videoPoster || article.featuredImage || undefined;
  const duration = article.videoDuration ? formatVideoDuration(article.videoDuration) : null;
  const durationPadded = article.videoDuration ? formatVideoDuration(article.videoDuration, true) : null;

  const retry = () => {
    setFailed(false);
    setAttempt((a) => a + 1);
    setPlaying(true);
  };

  return (
    <section aria-label={videoTitle} className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="inline-flex items-center gap-[6px] text-[18px] font-semibold leading-[30px] text-[#331751] lg:gap-2 lg:font-medium">
          <BlogIcon name="video-circle" width={18.333} height={18.333} className="hidden lg:inline-block" />
          <BlogIcon name="video-section" width={15} height={12} className="lg:hidden" />
          {videoTitle}
        </h2>
        {article.videoQuality && <span className="text-[12px] font-medium leading-5 text-[#5b5f66] lg:hidden">{article.videoQuality}</span>}
      </div>

      {failed ? (
        <div role="alert" className="flex flex-col items-center justify-center rounded-[12px] bg-[#1a1a1a] px-4 py-[33px] text-center">
          <BlogIcon name="video-error" width={32} height={32} className="mb-2" />
          <p className="pb-1 text-[12px] font-medium leading-4 text-white">خطا در پخش محتوای ویدیویی</p>
          <p className="pb-3 text-[10px] leading-[15px] text-[#9ca3af]">سرور پاسخ نداد یا فرمت پشتیبانی نمی‌شود</p>
          <button type="button" onClick={retry} className="inline-flex min-h-9 items-center gap-[6px] rounded-[4px] bg-white/20 px-3 py-[10px] text-[12px] font-medium text-white hover:bg-white/30">
            تلاش مجدد
            <BlogIcon name="retry-sm" width={14} height={14} />
          </button>
        </div>
      ) : (
        <div className="relative aspect-video w-full overflow-hidden rounded-[12px] border border-[#e9e8e5] bg-[#292333] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] lg:border-0">
          {playing ? (
            embedUrl ? (
              <iframe
                key={attempt}
                src={embedUrl}
                title={article.videoCaption || 'ویدیوی مقاله'}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 h-full w-full"
              />
            ) : (
              <video
                key={attempt}
                src={videoUrl}
                poster={poster}
                controls
                autoPlay
                playsInline
                preload="metadata"
                onError={() => setFailed(true)}
                className="absolute inset-0 h-full w-full bg-black"
              >
                <track kind="captions" />
              </video>
            )
          ) : (
            <>
              {poster && <img src={poster} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-85" />}
              <button
                type="button"
                onClick={() => setPlaying(true)}
                aria-label="پخش ویدیو"
                className="absolute inset-0 flex items-center justify-center bg-[rgba(51,23,81,0.3)] backdrop-blur-[0.5px] transition-colors hover:bg-[rgba(51,23,81,0.4)] lg:bg-[rgba(51,23,81,0.2)]"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#4a2e68] pl-[11px] pr-[13px] shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-4px_rgba(0,0,0,0.1)] lg:h-16 lg:w-16 lg:bg-white/90 lg:px-0">
                  <BlogIcon name="play" width={16.667} height={18.667} className="hidden lg:inline-block" style={{ marginRight: 2 }} />
                  <BlogIcon name="play-mobile" width={14.667} height={18.667} className="lg:hidden" />
                </span>
              </button>
              {duration && (
                <span className="absolute bottom-3 left-3 hidden items-center gap-1 rounded-full bg-[rgba(47,49,47,0.8)] px-[10px] py-[2px] text-[12px] font-medium leading-5 text-[#f2f1ee] backdrop-blur-[2px] lg:inline-flex">
                  <BlogIcon name="clock-white-xs" width={11.667} height={11.667} />
                  {duration}
                </span>
              )}
              {durationPadded && (
                <span className="absolute bottom-[10px] left-[10px] rounded-[4px] bg-[rgba(0,0,0,0.75)] px-2 py-[2px] text-[12px] font-medium leading-5 tracking-[0.3px] text-white lg:hidden">
                  {durationPadded}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {article.videoCaption && <p className="px-[2px] text-[12px] font-medium leading-5 text-[#5b5f66] lg:px-0 lg:pt-1 lg:leading-[19.5px]">{article.videoCaption}</p>}
    </section>
  );
};

export default ArticleVideo;
